import {
  Environment,
  PageType,
  ProductType,
  Region,
  StoreInformation,
  User,
} from './types/store-information'
import wcs from '@/scripts/libs/wcs'
import { NoEcommerce } from '../libs/no-ecommerce'
import { WcsLocales } from '../libs/wcs/types/locales'
import {
  DELIVERY_DETAILS_REDESIGN_VIEW,
  DELIVERY_DETAILS_VIEW,
  HOME_PAGE,
  MY_ADDRESSBOOK_VIEW,
  MY_DETAILS_VIEW,
  MY_ORDERS_VIEW,
  PAYMENT_DETAILS_VIEW,
  PAYMENT_REDESIGN_DETAILS_VIEW,
  PDP_DISPLAY_VIEW,
  RB_CATEGORY_DISPLAY_VIEW,
  SHOPCART_DISPLAY,
  SHOPCART_DISPLAY_VIEW,
  STATIC_PAGE_VIEW,
} from '../constants/pages'
import { CASE, CLIPON, KIT, OPTICAL, SUN } from '../constants/pdp'
import { WcsStoreId } from '../libs/wcs/types/store-id'
import { WcsUserData } from '../libs/wcs/user'
import { WcsAkamaiCookies } from '../libs/wcs/types/akamai-cookies'

export class StoreInformationMapper {
  static getStoreInformation = (
    productType: string,
    userData: WcsUserData,
    cartData: any
  ): StoreInformation => {
    const { storeId, catalogId, langId, locale, currency, viewTaskName } = wcs.getStoreConfig()

    const cartUrl = wcs.getEndPoint('shopCartView')
    const akamaiCC = NoEcommerce.getNoEcommerceCountry()
    return {
      storeId: Number(storeId),
      catalogID: Number(catalogId),
      environment: this.mapEnvironment(),
      region: this.mapRegion(storeId, akamaiCC),
      country: this.mapCountry(locale),
      timeZone: this.mapTimezone(),
      timestamp: this.getTimestamp(),
      lang: this.mapLang(locale),
      langID: Number(langId),
      currency,
      ecomm: !NoEcommerce.isStoreNoEcommerce(),
      user: this.mapUserData(userData),
      cart: this.mapCartData(cartData, cartUrl),
      touchDevice: this.isTouchEnabled(),
      pageType: this.mapPageType(viewTaskName),
      pageName: this.getPageName(),
      productType: this.mapProductType(productType),
      homePageURL: this.mapHomePageUrl(locale),
    }
  }

  private static mapCartData = (cartData: any, cartUrl: string) => {
    const numberOfItems = cartData.items.length
    const value = cartData.details.totalToNumber
    return {
      url: cartUrl,
      empty: numberOfItems <= 0,
      items: numberOfItems,
      value: value,
    }
  }

  private static mapUserData = (userData: WcsUserData): User => {
    return {
      logged: userData?.logged,
      name: userData?.name,
      mail: userData?.email,
    }
  }

  private static mapCountry = (locale: WcsLocales) => {
    return locale?.toString()?.split('-')[1]?.toLowerCase()
  }

  private static mapLang = (locale: WcsLocales) => {
    return locale?.toString()?.split('-')[0]?.toLowerCase()
  }

  private static getTimestamp(): number {
    return Date.now()
  }

  private static mapHomePageUrl(locale: string): string {
    const origin = window.location.origin
    const currentStore = WcsLocales.mapCurrentStore(locale)
    return `${origin}/${currentStore}`
  }

  static mapEnvironment(): Environment {
    const origin = window?.location.origin
    if (origin.includes('integration.')) {
      return Environment.INT
    } else if (origin.includes('acceptance')) {
      return Environment.UAT
    } else if (origin.includes('www.')) {
      return Environment.PROD
    }
    return Environment.INT
  }

  private static mapTimezone(): number {
    const date = new Date()
    const offset = date.getTimezoneOffset()
    return offset / 60
  }

  static mapPageType(viewTaskName: string): PageType {
    switch (viewTaskName) {
      case HOME_PAGE:
        return PageType.HP
      case RB_CATEGORY_DISPLAY_VIEW:
        return PageType.PLP
      case PDP_DISPLAY_VIEW:
        return PageType.PDP
      case STATIC_PAGE_VIEW:
        return PageType.LP
      case SHOPCART_DISPLAY_VIEW:
      case SHOPCART_DISPLAY:
        return PageType.CART
      case DELIVERY_DETAILS_VIEW:
      case PAYMENT_DETAILS_VIEW:
      case DELIVERY_DETAILS_REDESIGN_VIEW:
      case PAYMENT_REDESIGN_DETAILS_VIEW:
        return PageType.CHECKOUT
      case MY_DETAILS_VIEW:
      case MY_ORDERS_VIEW:
      case MY_ADDRESSBOOK_VIEW:
        return PageType.MY_ACCOUNT
    }
    return PageType.HP
  }

  static mapProductType(type: string): ProductType | string {
    switch (type) {
      case SUN:
        return ProductType.SUNGLASSES
      case OPTICAL:
        return ProductType.EYEGLASSES
      case '':
        return ProductType.EVERGLASSES
      case KIT:
      case CASE:
        return ProductType.ACCESSORIES
      case CLIPON:
        return ProductType.CLIP_ON
      default:
        return type
    }
  }

  static mapRegion(storeId: string | undefined, akamaiCC: string | undefined | null): Region {
    if (storeId) {
      if (WcsStoreId.EUROPEAN_COUNTRIES.includes(storeId)) {
        return Region.EUROPE
      } else if (WcsStoreId.NORTH_AMERICA_COUNTRIES.includes(storeId)) {
        return Region.NORTH_AMERICA
      } else if (WcsStoreId.SOUTH_AMERICA_COUNTRIES.includes(storeId)) {
        return Region.SOUTH_AMERICA
      } else if (WcsStoreId.ASIAN_COUNTRIES.includes(storeId)) {
        return Region.ASIA
      } else if (WcsStoreId.AFRICAN_COUNTRIES.includes(storeId)) {
        return Region.AFRICA
      } else if (WcsStoreId.OCEANIA_COUNTRIES.includes(storeId)) {
        return Region.OCEANIA
      }
    } else if (akamaiCC) {
      if (WcsAkamaiCookies.EUROPEAN_COUNTRIES.includes(akamaiCC)) {
        return Region.EUROPE
      } else if (WcsAkamaiCookies.SOUTH_AMERICA_COUNTRIES.includes(akamaiCC)) {
        return Region.SOUTH_AMERICA
      } else if (WcsAkamaiCookies.ASIAN_COUNTRIES.includes(akamaiCC)) {
        return Region.ASIA
      } else if (WcsAkamaiCookies.AFRICAN_COUNTRIES.includes(akamaiCC)) {
        return Region.AFRICA
      }
    }
    return Region.NORTH_AMERICA
  }
  private static isTouchEnabled(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0
  }

  private static getPageName(): string {
    const pageName = window?.utag_data?.Page_Type
    return pageName
  }
}
